// Savings benchmarks by age — ages 16–75
// Sources: Fidelity retirement guidelines, Fed Survey of Consumer Finances, EBRI
// Assumes median US household income ~$78,000 for salary multiples

// const MEDIAN_INCOME = 78000;

const savingsByAge = {
  16: {
    age: 16,
    medianSavings: 500,
    recommendedSavings: 1000,
    salaryMultiple: 0,
    emergencyFundTarget: 2000,
    retirementTarget: 0,
    retirementGuideline: 'Open a Roth IRA if you have earned income. Even $500 invested at 16 can grow to $30,000+ by retirement.',
    context: 'At 16, most income comes from part-time jobs. The single best move is to save 50% of every paycheck and open a Roth IRA if you have earned income — contributions are limited to what you actually earn.',
    nextMilestone: { age: 18, target: 3000, label: '$3k saved by 18' },
  },
  17: {
    age: 17,
    medianSavings: 800,
    recommendedSavings: 1500,
    salaryMultiple: 0,
    emergencyFundTarget: 2500,
    retirementTarget: 0,
    retirementGuideline: 'If you have earned income, contribute to a Roth IRA. Even $1,000 at 17 compounds to $50,000+ by age 65.',
    context: 'At 17, focus on saving for college, a car, or your first apartment. If you have any earned income, a parent-custodial Roth IRA is one of the best financial decisions you can make.',
    nextMilestone: { age: 18, target: 3000, label: '$3k saved by 18' },
  },
  18: {
    age: 18,
    medianSavings: 1200,
    recommendedSavings: 3000,
    salaryMultiple: 0,
    emergencyFundTarget: 4000,
    retirementTarget: 500,
    retirementGuideline: 'Open your own Roth IRA at 18. The IRS allows contributions up to your total earned income (max $7,000 in 2025).',
    context: 'At 18 you can open financial accounts independently. Prioritize: emergency fund, avoid credit card debt, and start a Roth IRA if working. Time is your greatest advantage.',
    nextMilestone: { age: 20, target: 6000, label: '$6k saved by 20' },
  },
  19: {
    age: 19,
    medianSavings: 2000,
    recommendedSavings: 5000,
    salaryMultiple: 0,
    emergencyFundTarget: 6000,
    retirementTarget: 1500,
    retirementGuideline: 'Max your Roth IRA every year you can. $7,000/year starting at 19 grows to over $1.5M by 65 at 8% average returns.',
    context: 'Whether you\'re in college or working full-time, building your savings habit now pays off for decades. Automate savings so you don\'t spend it first.',
    nextMilestone: { age: 22, target: 10000, label: '$10k saved by 22' },
  },
  20: {
    age: 20,
    medianSavings: 3000,
    recommendedSavings: 7000,
    salaryMultiple: 0,
    emergencyFundTarget: 8000,
    retirementTarget: 3000,
    retirementGuideline: 'Contribute to a Roth IRA and avoid lifestyle inflation. Every dollar saved in your 20s is worth roughly $10 saved in your 40s.',
    context: 'At 20, compound interest is your superpower. Even $100/month invested now grows to $350,000+ by retirement. Building the savings habit beats the savings amount right now.',
    nextMilestone: { age: 22, target: 10000, label: '$10k saved by 22' },
  },
  21: {
    age: 21,
    medianSavings: 4000,
    recommendedSavings: 10000,
    salaryMultiple: 0,
    emergencyFundTarget: 10000,
    retirementTarget: 5000,
    retirementGuideline: 'Max your Roth IRA ($7,000 in 2025) if you can. If your employer offers a 401(k), contribute at least enough to get the full match.',
    context: 'If you\'re entering the workforce, get your employer\'s 401(k) match first — it\'s an instant 50–100% return. Then build your emergency fund to 3 months of expenses.',
    nextMilestone: { age: 25, target: 15000, label: '$15k saved by 25' },
  },
  22: {
    age: 22,
    medianSavings: 3500,
    recommendedSavings: 0,
    salaryMultiple: 0,
    emergencyFundTarget: 12000,         // 3 months expenses (~$4k/mo)
    retirementTarget: 0,
    retirementGuideline: 'Start contributing to your 401(k) or Roth IRA now, even if it\'s just 3%. Time is your biggest asset.',
    context: 'Most 22-year-olds are just entering the workforce. The goal right now is to build your emergency fund and start any retirement contribution — even small.',
    nextMilestone: { age: 25, target: 10000, label: '$10k saved by 25' },
  },
  23: {
    age: 23,
    medianSavings: 4500,
    recommendedSavings: 5000,
    salaryMultiple: 0,
    emergencyFundTarget: 12000,
    retirementTarget: 3000,
    retirementGuideline: 'Aim to have at least 1 month of salary in retirement accounts by 23.',
    context: 'Building your emergency fund and getting your employer 401(k) match are the two highest-ROI moves you can make right now.',
    nextMilestone: { age: 25, target: 15000, label: '$15k total saved by 25' },
  },
  24: {
    age: 24,
    medianSavings: 6000,
    recommendedSavings: 8000,
    salaryMultiple: 0,
    emergencyFundTarget: 15000,
    retirementTarget: 5000,
    retirementGuideline: 'Maximize your employer match — it\'s an instant 50–100% return on your contributions.',
    context: 'Focus on eliminating high-interest debt and building 3–6 months of expenses in an emergency fund alongside retirement contributions.',
    nextMilestone: { age: 25, target: 15000, label: '$15k total saved by 25' },
  },
  25: {
    age: 25,
    medianSavings: 8500,
    recommendedSavings: 15000,
    salaryMultiple: 0.25,
    emergencyFundTarget: 18000,
    retirementTarget: 8000,
    retirementGuideline: 'By 25, target having at least 25% of your annual salary saved for retirement.',
    context: 'A 25-year-old who invests $7,000/year in a Roth IRA could have $1M+ by retirement thanks to compound growth. The math strongly favors starting now.',
    nextMilestone: { age: 30, target: 78000, label: '1× salary by 30' },
  },
  26: {
    age: 26,
    medianSavings: 11000,
    recommendedSavings: 20000,
    salaryMultiple: 0.35,
    emergencyFundTarget: 18000,
    retirementTarget: 12000,
    retirementGuideline: 'At 26, target roughly 35% of your annual salary saved for retirement.',
    context: 'Now is a great time to automate savings. Set up automatic transfers on payday so saving happens before you can spend it.',
    nextMilestone: { age: 30, target: 78000, label: '1× salary by 30' },
  },
  27: {
    age: 27,
    medianSavings: 14000,
    recommendedSavings: 26000,
    salaryMultiple: 0.5,
    emergencyFundTarget: 21000,
    retirementTarget: 18000,
    retirementGuideline: 'At 27, target half your annual salary saved for retirement.',
    context: 'If you\'re behind, don\'t panic. Increasing your savings rate by even 1% per year can close the gap significantly over time.',
    nextMilestone: { age: 30, target: 78000, label: '1× salary by 30' },
  },
  28: {
    age: 28,
    medianSavings: 18000,
    recommendedSavings: 35000,
    salaryMultiple: 0.65,
    emergencyFundTarget: 21000,
    retirementTarget: 25000,
    retirementGuideline: 'At 28, you should be approaching 65–70% of your annual salary in retirement savings.',
    context: 'This is often when life gets expensive — weddings, homes, kids. Keep retirement contributions going even if you can\'t max them out.',
    nextMilestone: { age: 30, target: 78000, label: '1× salary by 30' },
  },
  29: {
    age: 29,
    medianSavings: 22000,
    recommendedSavings: 55000,
    salaryMultiple: 0.85,
    emergencyFundTarget: 24000,
    retirementTarget: 40000,
    retirementGuideline: 'At 29, target 85% of your annual salary saved for retirement.',
    context: 'The last year of your 20s. If you\'re not at 1× your salary yet, focus on increasing income and saving the difference.',
    nextMilestone: { age: 30, target: 78000, label: '1× salary by 30' },
  },
  30: {
    age: 30,
    medianSavings: 28000,
    recommendedSavings: 78000,
    salaryMultiple: 1,
    emergencyFundTarget: 24000,
    retirementTarget: 60000,
    retirementGuideline: 'Fidelity recommends having 1× your salary saved by 30.',
    context: 'The median 30-year-old has $28k saved — well below the 1× salary target. If you\'re behind, you\'re in good company, but now is the time to accelerate.',
    nextMilestone: { age: 40, target: 234000, label: '3× salary by 40' },
  },
  31: {
    age: 31,
    medianSavings: 33000,
    recommendedSavings: 90000,
    salaryMultiple: 1.15,
    emergencyFundTarget: 24000,
    retirementTarget: 70000,
    retirementGuideline: 'At 31, target 1.15× your annual salary in retirement savings.',
    context: 'Income typically rises in your 30s. Try to save at least 15% of gross income including employer match.',
    nextMilestone: { age: 40, target: 234000, label: '3× salary by 40' },
  },
  32: {
    age: 32,
    medianSavings: 38000,
    recommendedSavings: 105000,
    salaryMultiple: 1.35,
    emergencyFundTarget: 24000,
    retirementTarget: 82000,
    retirementGuideline: 'At 32, target 1.35× your annual salary in retirement savings.',
    context: 'Lifestyle inflation is the enemy in your 30s. Every raise is an opportunity to increase your savings rate.',
    nextMilestone: { age: 40, target: 234000, label: '3× salary by 40' },
  },
  33: {
    age: 33,
    medianSavings: 44000,
    recommendedSavings: 120000,
    salaryMultiple: 1.5,
    emergencyFundTarget: 27000,
    retirementTarget: 95000,
    retirementGuideline: 'At 33, target 1.5× your annual salary in retirement savings.',
    context: 'If you haven\'t already, consider a financial plan. At 33, small adjustments now have decades to compound.',
    nextMilestone: { age: 40, target: 234000, label: '3× salary by 40' },
  },
  34: {
    age: 34,
    medianSavings: 52000,
    recommendedSavings: 140000,
    salaryMultiple: 1.75,
    emergencyFundTarget: 27000,
    retirementTarget: 110000,
    retirementGuideline: 'At 34, target 1.75× your annual salary in retirement savings.',
    context: 'You\'re halfway through the journey to the 3× salary milestone. Keep your eye on the ball.',
    nextMilestone: { age: 40, target: 234000, label: '3× salary by 40' },
  },
  35: {
    age: 35,
    medianSavings: 60000,
    recommendedSavings: 156000,
    salaryMultiple: 2,
    emergencyFundTarget: 27000,
    retirementTarget: 120000,
    retirementGuideline: 'At 35, target 2× your annual salary in retirement savings.',
    context: 'At 35, $60k median savings is common but below targets. If you have 2× salary saved, you\'re on track for a comfortable retirement.',
    nextMilestone: { age: 40, target: 234000, label: '3× salary by 40' },
  },
  36: {
    age: 36,
    medianSavings: 68000,
    recommendedSavings: 175000,
    salaryMultiple: 2.25,
    emergencyFundTarget: 30000,
    retirementTarget: 138000,
    retirementGuideline: 'At 36, target 2.25× your annual salary in retirement savings.',
    context: 'If you\'re behind on savings, consider whether you can increase your 401(k) contribution by just 1%. That small change compounds significantly over 30 years.',
    nextMilestone: { age: 40, target: 234000, label: '3× salary by 40' },
  },
  37: {
    age: 37,
    medianSavings: 76000,
    recommendedSavings: 195000,
    salaryMultiple: 2.5,
    emergencyFundTarget: 30000,
    retirementTarget: 155000,
    retirementGuideline: 'At 37, target 2.5× your annual salary in retirement savings.',
    context: 'Your late 30s are prime earning years. Maxing your 401(k) ($23,500 in 2025) if possible can close savings gaps fast.',
    nextMilestone: { age: 40, target: 234000, label: '3× salary by 40' },
  },
  38: {
    age: 38,
    medianSavings: 84000,
    recommendedSavings: 214000,
    salaryMultiple: 2.75,
    emergencyFundTarget: 30000,
    retirementTarget: 170000,
    retirementGuideline: 'At 38, target 2.75× your annual salary in retirement savings.',
    context: 'Many 38-year-olds are balancing college savings for kids with retirement. Prioritize retirement first — you can borrow for college but not for retirement.',
    nextMilestone: { age: 40, target: 234000, label: '3× salary by 40' },
  },
  39: {
    age: 39,
    medianSavings: 92000,
    recommendedSavings: 225000,
    salaryMultiple: 2.9,
    emergencyFundTarget: 30000,
    retirementTarget: 180000,
    retirementGuideline: 'At 39, target ~2.9× your annual salary in retirement savings.',
    context: 'One year from the 3× milestone. If you\'re behind, a one-time lump sum from a bonus or tax refund invested now can make a real difference.',
    nextMilestone: { age: 40, target: 234000, label: '3× salary by 40' },
  },
  40: {
    age: 40,
    medianSavings: 100000,
    recommendedSavings: 234000,
    salaryMultiple: 3,
    emergencyFundTarget: 30000,
    retirementTarget: 195000,
    retirementGuideline: 'Fidelity recommends having 3× your salary saved by 40.',
    context: 'The median 40-year-old has $100k saved — but the 3× salary benchmark is $234k at the median income. If you\'re behind, this is your wake-up call to accelerate.',
    nextMilestone: { age: 50, target: 468000, label: '6× salary by 50' },
  },
  41: {
    age: 41,
    medianSavings: 112000,
    recommendedSavings: 260000,
    salaryMultiple: 3.25,
    emergencyFundTarget: 33000,
    retirementTarget: 215000,
    retirementGuideline: 'At 41, target 3.25× your annual salary in retirement savings.',
    context: 'Your 40s are when the compound interest magic becomes visible in your statements. Stay consistent.',
    nextMilestone: { age: 50, target: 468000, label: '6× salary by 50' },
  },
  42: {
    age: 42,
    medianSavings: 125000,
    recommendedSavings: 288000,
    salaryMultiple: 3.5,
    emergencyFundTarget: 33000,
    retirementTarget: 235000,
    retirementGuideline: 'At 42, target 3.5× your annual salary in retirement savings.',
    context: 'At this point, account balances should be growing faster from investment returns than from contributions. That\'s compound interest working for you.',
    nextMilestone: { age: 50, target: 468000, label: '6× salary by 50' },
  },
  43: {
    age: 43,
    medianSavings: 140000,
    recommendedSavings: 316000,
    salaryMultiple: 4,
    emergencyFundTarget: 33000,
    retirementTarget: 260000,
    retirementGuideline: 'At 43, target 4× your annual salary in retirement savings.',
    context: 'If you\'re at 4× salary, you\'re ahead of most Americans. Keep it up and consider whether you\'re on track to retire when you want.',
    nextMilestone: { age: 50, target: 468000, label: '6× salary by 50' },
  },
  44: {
    age: 44,
    medianSavings: 155000,
    recommendedSavings: 345000,
    salaryMultiple: 4.25,
    emergencyFundTarget: 33000,
    retirementTarget: 285000,
    retirementGuideline: 'At 44, target 4.25× your annual salary in retirement savings.',
    context: 'Less than 10 years from the 6× milestone. Keep saving aggressively — any extra income should flow into tax-advantaged accounts first.',
    nextMilestone: { age: 50, target: 468000, label: '6× salary by 50' },
  },
  45: {
    age: 45,
    medianSavings: 172000,
    recommendedSavings: 375000,
    salaryMultiple: 4.5,
    emergencyFundTarget: 36000,
    retirementTarget: 312000,
    retirementGuideline: 'At 45, target 4.5× your annual salary in retirement savings.',
    context: 'At 45, you have roughly 20 years until traditional retirement. Even a $100,000 balance today grows to $670,000 at 10% annual returns by 65.',
    nextMilestone: { age: 50, target: 468000, label: '6× salary by 50' },
  },
  46: {
    age: 46,
    medianSavings: 189000,
    recommendedSavings: 406000,
    salaryMultiple: 5,
    emergencyFundTarget: 36000,
    retirementTarget: 340000,
    retirementGuideline: 'At 46, target 5× your annual salary in retirement savings.',
    context: 'Peak earning years are typically 45–55. If you\'re not maxing your retirement accounts now, you may regret it later.',
    nextMilestone: { age: 50, target: 468000, label: '6× salary by 50' },
  },
  47: {
    age: 47,
    medianSavings: 206000,
    recommendedSavings: 428000,
    salaryMultiple: 5.25,
    emergencyFundTarget: 36000,
    retirementTarget: 365000,
    retirementGuideline: 'At 47, target 5.25× your annual salary in retirement savings.',
    context: 'Catch-up contributions are available at 50 ($7,500 extra in 401k). Start planning to use them in 3 years.',
    nextMilestone: { age: 50, target: 468000, label: '6× salary by 50' },
  },
  48: {
    age: 48,
    medianSavings: 224000,
    recommendedSavings: 445000,
    salaryMultiple: 5.5,
    emergencyFundTarget: 36000,
    retirementTarget: 390000,
    retirementGuideline: 'At 48, target 5.5× your annual salary in retirement savings.',
    context: 'Two years until catch-up contributions kick in. Evaluate your projected retirement income vs. expected expenses.',
    nextMilestone: { age: 50, target: 468000, label: '6× salary by 50' },
  },
  49: {
    age: 49,
    medianSavings: 243000,
    recommendedSavings: 458000,
    salaryMultiple: 5.75,
    emergencyFundTarget: 36000,
    retirementTarget: 415000,
    retirementGuideline: 'At 49, target 5.75× your annual salary in retirement savings.',
    context: 'One year from catch-up contributions. Also review your investment allocation — it may be time to start slightly reducing risk.',
    nextMilestone: { age: 50, target: 468000, label: '6× salary by 50' },
  },
  50: {
    age: 50,
    medianSavings: 262000,
    recommendedSavings: 468000,
    salaryMultiple: 6,
    emergencyFundTarget: 36000,
    retirementTarget: 450000,
    retirementGuideline: 'Fidelity recommends having 6× your salary saved by 50.',
    context: 'At 50, you unlock catch-up contributions: an extra $7,500/year in your 401(k) and an extra $1,000 in your IRA. Use them. You have ~15 years of peak compounding left.',
    nextMilestone: { age: 60, target: 624000, label: '8× salary by 60' },
  },
  51: {
    age: 51,
    medianSavings: 278000,
    recommendedSavings: 500000,
    salaryMultiple: 6.25,
    emergencyFundTarget: 39000,
    retirementTarget: 480000,
    retirementGuideline: 'At 51, target 6.25× your annual salary in retirement savings.',
    context: 'You can now contribute up to $31,000/year to your 401(k) including catch-up. This is a powerful wealth-building window.',
    nextMilestone: { age: 60, target: 624000, label: '8× salary by 60' },
  },
  52: {
    age: 52,
    medianSavings: 293000,
    recommendedSavings: 530000,
    salaryMultiple: 6.5,
    emergencyFundTarget: 39000,
    retirementTarget: 510000,
    retirementGuideline: 'At 52, target 6.5× your annual salary in retirement savings.',
    context: 'Your 50s are your wealth-building prime. High income, lower child-related expenses, and catch-up contributions create ideal savings conditions.',
    nextMilestone: { age: 60, target: 624000, label: '8× salary by 60' },
  },
  53: {
    age: 53,
    medianSavings: 310000,
    recommendedSavings: 560000,
    salaryMultiple: 6.75,
    emergencyFundTarget: 39000,
    retirementTarget: 540000,
    retirementGuideline: 'At 53, target 6.75× your annual salary in retirement savings.',
    context: 'Consider meeting with a fee-only financial advisor for a retirement projection. At 53, you can still make significant changes to your trajectory.',
    nextMilestone: { age: 60, target: 624000, label: '8× salary by 60' },
  },
  54: {
    age: 54,
    medianSavings: 326000,
    recommendedSavings: 590000,
    salaryMultiple: 7,
    emergencyFundTarget: 39000,
    retirementTarget: 570000,
    retirementGuideline: 'At 54, target 7× your annual salary in retirement savings.',
    context: 'At 54, Social Security strategy matters. Delaying from 62 to 70 can increase your monthly benefit by 77%. Factor this into your plan.',
    nextMilestone: { age: 60, target: 624000, label: '8× salary by 60' },
  },
  55: {
    age: 55,
    medianSavings: 345000,
    recommendedSavings: 585000,
    salaryMultiple: 7.5,
    emergencyFundTarget: 42000,
    retirementTarget: 600000,
    retirementGuideline: 'At 55, target 7.5× your annual salary in retirement savings.',
    context: 'At 55 you can access 401(k) funds penalty-free if you leave your employer. This is the "Rule of 55" — useful if early retirement is on your radar.',
    nextMilestone: { age: 60, target: 624000, label: '8× salary by 60' },
  },
  56: {
    age: 56,
    medianSavings: 365000,
    recommendedSavings: 610000,
    salaryMultiple: 7.5,
    emergencyFundTarget: 42000,
    retirementTarget: 630000,
    retirementGuideline: 'At 56, target 7.5× your annual salary in retirement savings.',
    context: 'Healthcare costs in retirement are one of the biggest unknowns. If you have access to an HSA, max it out — it\'s triple tax-advantaged.',
    nextMilestone: { age: 60, target: 624000, label: '8× salary by 60' },
  },
  57: {
    age: 57,
    medianSavings: 385000,
    recommendedSavings: 624000,
    salaryMultiple: 8,
    emergencyFundTarget: 42000,
    retirementTarget: 660000,
    retirementGuideline: 'At 57, you\'re approaching the 8× salary milestone. Stay focused.',
    context: 'Begin thinking concretely about retirement income: Social Security, pension, RMDs, and withdrawal strategy. A plan now prevents costly mistakes later.',
    nextMilestone: { age: 60, target: 624000, label: '8× salary by 60' },
  },
  58: {
    age: 58,
    medianSavings: 406000,
    recommendedSavings: 624000,
    salaryMultiple: 8,
    emergencyFundTarget: 42000,
    retirementTarget: 690000,
    retirementGuideline: 'At 58, target 8× your annual salary in retirement savings.',
    context: 'You\'re within 7 years of traditional retirement. Consider having a formal retirement projection done by a CFP.',
    nextMilestone: { age: 60, target: 624000, label: '8× salary by 60' },
  },
  59: {
    age: 59,
    medianSavings: 427000,
    recommendedSavings: 624000,
    salaryMultiple: 8,
    emergencyFundTarget: 45000,
    retirementTarget: 720000,
    retirementGuideline: 'At 59, target 8× your annual salary in retirement savings. You can now access IRA funds penalty-free (age 59½).',
    context: 'At 59½, penalty-free IRA and 401(k) withdrawals begin. This opens Roth conversion strategies that can reduce future Required Minimum Distributions.',
    nextMilestone: { age: 65, target: 780000, label: '10× salary by 65' },
  },
  60: {
    age: 60,
    medianSavings: 450000,
    recommendedSavings: 624000,
    salaryMultiple: 8,
    emergencyFundTarget: 45000,
    retirementTarget: 750000,
    retirementGuideline: 'Fidelity recommends having 8× your salary saved by 60.',
    context: 'At 60, you should have a concrete retirement date and income plan. The 4% rule: a $1M portfolio supports ~$40k/year in withdrawals. Adjust to your target income.',
    nextMilestone: { age: 65, target: 780000, label: '10× salary by 65' },
  },
  61: {
    age: 61,
    medianSavings: 472000,
    recommendedSavings: 664000,
    salaryMultiple: 8.5,
    emergencyFundTarget: 45000,
    retirementTarget: 780000,
    retirementGuideline: 'At 61, target 8.5× your annual salary in retirement savings.',
    context: 'Four years until Medicare eligibility (65). If retiring before 65, plan for private health insurance — it can cost $600–$1,500/month per person.',
    nextMilestone: { age: 65, target: 780000, label: '10× salary by 65' },
  },
  62: {
    age: 62,
    medianSavings: 494000,
    recommendedSavings: 702000,
    salaryMultiple: 9,
    emergencyFundTarget: 45000,
    retirementTarget: 810000,
    retirementGuideline: 'At 62, target 9× your annual salary. Social Security is available at 62, but benefits are reduced up to 30% vs. waiting until 67.',
    context: 'Age 62 is the earliest you can claim Social Security, but claiming early permanently reduces your benefit. For every year you wait (up to 70), benefits grow 6–8%.',
    nextMilestone: { age: 65, target: 780000, label: '10× salary by 65' },
  },
  63: {
    age: 63,
    medianSavings: 515000,
    recommendedSavings: 741000,
    salaryMultiple: 9.5,
    emergencyFundTarget: 45000,
    retirementTarget: 840000,
    retirementGuideline: 'At 63, target 9.5× your annual salary in retirement savings.',
    context: 'Two years from traditional Medicare eligibility. Begin comparing Medicare Supplement and Medicare Advantage plans to understand your future healthcare costs.',
    nextMilestone: { age: 65, target: 780000, label: '10× salary by 65' },
  },
  64: {
    age: 64,
    medianSavings: 535000,
    recommendedSavings: 760000,
    salaryMultiple: 9.75,
    emergencyFundTarget: 48000,
    retirementTarget: 870000,
    retirementGuideline: 'At 64, target 9.75× your annual salary in retirement savings.',
    context: 'One year from Medicare. Finalize your Social Security claiming strategy. For every year you delay past 62, your monthly benefit grows substantially.',
    nextMilestone: { age: 65, target: 780000, label: '10× salary at 65' },
  },
  65: {
    age: 65,
    medianSavings: 555000,
    recommendedSavings: 780000,
    salaryMultiple: 10,
    emergencyFundTarget: 48000,
    retirementTarget: 900000,
    retirementGuideline: 'At 65, Fidelity\'s extended guideline suggests 10× your salary. Medicare eligibility begins — enroll within your enrollment window to avoid lifelong premium penalties.',
    context: 'A $780k nest egg at 65 supports roughly $31k/year in withdrawals (4% rule), plus Social Security income. The average Social Security benefit in 2025 is ~$1,900/month ($22,800/year).',
    nextMilestone: { age: 70, target: 900000, label: 'Maintain 10–12× by 70' },
  },
  66: {
    age: 66,
    medianSavings: 570000,
    recommendedSavings: 810000,
    salaryMultiple: 10,
    emergencyFundTarget: 48000,
    retirementTarget: 900000,
    retirementGuideline: 'Full Social Security benefits begin at 66 for those born 1943–1954. Delaying to 70 increases your monthly benefit by ~8% per year.',
    context: 'At 66, evaluate whether to claim Social Security now or delay. Each year you delay (up to 70) increases your lifetime benefit by roughly 8%. If you have savings to bridge the gap, waiting pays off for many.',
    nextMilestone: null,
  },
  67: {
    age: 67,
    medianSavings: 580000,
    recommendedSavings: 820000,
    salaryMultiple: 10,
    emergencyFundTarget: 48000,
    retirementTarget: 900000,
    retirementGuideline: 'Full retirement age (FRA) for those born after 1960 is 67. Claiming Social Security at FRA gives you 100% of your earned benefit.',
    context: 'At 67 (full retirement age for most), focus on sustainable withdrawal rates. The 4% rule suggests withdrawing no more than 4% annually to make your savings last 30 years.',
    nextMilestone: null,
  },
  68: {
    age: 68,
    medianSavings: 585000,
    recommendedSavings: 820000,
    salaryMultiple: 10,
    emergencyFundTarget: 48000,
    retirementTarget: 900000,
    retirementGuideline: 'If you haven\'t claimed Social Security yet, your benefit grows ~8%/year by delaying. At 68 vs. 67 full retirement age, you\'re already 8% ahead.',
    context: 'At 68, RMDs (Required Minimum Distributions) are still two years away. Review your asset allocation — most retirees gradually shift to a higher bond percentage to reduce sequence-of-returns risk.',
    nextMilestone: null,
  },
  69: {
    age: 69,
    medianSavings: 590000,
    recommendedSavings: 820000,
    salaryMultiple: 10,
    emergencyFundTarget: 48000,
    retirementTarget: 900000,
    retirementGuideline: 'One year from maximum Social Security — delaying to 70 locks in the highest possible monthly benefit for life.',
    context: 'At 69, plan for Required Minimum Distributions which begin at 73 (per SECURE Act 2.0). RMDs are based on your account balance and IRS life expectancy tables. Failing to take them incurs a 25% penalty.',
    nextMilestone: { age: 70, target: 840000, label: 'Max Social Security at 70' },
  },
  70: {
    age: 70,
    medianSavings: 600000,
    recommendedSavings: 840000,
    salaryMultiple: 10,
    emergencyFundTarget: 48000,
    retirementTarget: 900000,
    retirementGuideline: 'Age 70 is the latest you can delay Social Security — you\'ve now locked in the maximum possible monthly benefit (up to 32% more than claiming at 62).',
    context: 'At 70, Social Security has maxed out — claim it now if you haven\'t already. Your withdrawal strategy matters more than ever. Consider a "bucket" approach: 1–2 years in cash, 3–10 years in bonds, rest in equities.',
    nextMilestone: null,
  },
  71: {
    age: 71,
    medianSavings: 595000,
    recommendedSavings: 820000,
    salaryMultiple: 10,
    emergencyFundTarget: 48000,
    retirementTarget: 850000,
    retirementGuideline: 'Focus on tax-efficient withdrawals. Draw from taxable accounts first to let tax-advantaged accounts continue growing.',
    context: 'Portfolio longevity is the priority at 71. Healthcare costs rise significantly after 70 — long-term care insurance or a dedicated healthcare savings bucket can protect your nest egg.',
    nextMilestone: null,
  },
  72: {
    age: 72,
    medianSavings: 585000,
    recommendedSavings: 810000,
    salaryMultiple: 10,
    emergencyFundTarget: 48000,
    retirementTarget: 840000,
    retirementGuideline: 'RMDs begin at 73 (SECURE Act 2.0). Start planning your withdrawal strategy now so you aren\'t forced into a higher tax bracket.',
    context: 'At 72, review beneficiary designations on all retirement accounts. A Roth conversion (moving traditional IRA funds to a Roth) before RMDs begin can reduce lifetime tax burden for heirs.',
    nextMilestone: { age: 73, target: 800000, label: 'RMDs begin at 73' },
  },
  73: {
    age: 73,
    medianSavings: 575000,
    recommendedSavings: 800000,
    salaryMultiple: 10,
    emergencyFundTarget: 48000,
    retirementTarget: 820000,
    retirementGuideline: 'Required Minimum Distributions begin at 73 (SECURE Act 2.0). Your RMD amount is calculated by dividing your account balance by the IRS life expectancy factor for your age.',
    context: 'At 73, you must begin taking RMDs from traditional IRAs and 401(k)s. The penalty for missing an RMD is 25% of the amount not taken (reduced to 10% if corrected quickly). Use an RMD calculator to plan.',
    nextMilestone: null,
  },
  74: {
    age: 74,
    medianSavings: 560000,
    recommendedSavings: 780000,
    salaryMultiple: 10,
    emergencyFundTarget: 48000,
    retirementTarget: 800000,
    retirementGuideline: 'Review your RMD strategy annually. Your life expectancy divisor changes each year, affecting how much you must withdraw.',
    context: 'At 74, estate planning becomes increasingly important. Ensure your will, beneficiary designations, power of attorney, and healthcare directives are current. These documents protect your family and your wishes.',
    nextMilestone: null,
  },
  75: {
    age: 75,
    medianSavings: 540000,
    recommendedSavings: 760000,
    salaryMultiple: 10,
    emergencyFundTarget: 48000,
    retirementTarget: 780000,
    retirementGuideline: 'At 75, focus on preserving capital and income predictability. Consider annuities for guaranteed lifetime income to supplement Social Security.',
    context: 'A 75-year-old with $750,000 drawing 4% annually receives $30,000/year from savings, plus Social Security. The average benefit at 75 is roughly $2,100/month. Together, that\'s a solid income floor for most retirees.',
    nextMilestone: null,
  },
};

export function getSavingsByAge(age) {
  const ageNum = parseInt(age, 10);
  return savingsByAge[ageNum] || null;
}

export const ALL_AGES = Object.keys(savingsByAge).map(Number);
export default savingsByAge;
